import taxonomyData from '../data/diagramloom_taxonomy.json';

// Preset packs based on scale-aware architecture checklist
const PRESET_PACKS = {
  'Starter': {
    name: 'Starter Pack (Tier A)',
    description: 'Small e-commerce / SaaS - Start lean, scale ceremony with risk',
    maxAutoSelect: 8,
    coreArtifacts: ['PRD', 'SRS', 'C4-CTX', 'C4-CON', 'C4-DEP', 'ADR', 'RTM', 'TS']
  },
  'Serious': {
    name: 'Serious Product (Tier B)', 
    description: 'Payments or PII - Everything in Tier A plus threat modeling',
    maxAutoSelect: 11,
    coreArtifacts: ['PRD', 'SRS', 'C4-CTX', 'C4-CON', 'C4-DEP', 'ADR', 'RTM', 'TS', 'TM', 'PIA', 'OAS']
  },
  'Regulated': {
    name: 'Regulated Enterprise (Tier C)',
    description: 'Banking/highly regulated - Everything in Tier B plus compliance controls',
    maxAutoSelect: 14,
    coreArtifacts: ['PRD', 'SRS', 'C4-CTX', 'C4-CON', 'C4-DEP', 'ADR', 'RTM', 'TS', 'TM', 'PIA', 'OAS', 'SSP', 'DRP', 'BCP']
  }
};

// C4 bundle for UI grouping
const C4_BUNDLE = {
  id: 'C4-BUNDLE',
  name: 'C4 Architecture Views',
  description: 'Context, Container, and Deployment diagrams as a complete set',
  children: ['C4-CTX', 'C4-CON', 'C4-DEP']
};

// Difficulty heuristics
const DIFFICULTY_MAP = {
  'PRD': 'Easy', 'SRS': 'Medium', 'C4-CTX': 'Easy', 'C4-CON': 'Medium', 'C4-DEP': 'Medium',
  'UML-SEQ': 'Medium', 'OAS': 'Medium', 'TM': 'Advanced', 'ADR': 'Easy', 'RTM': 'Medium',
  'TS': 'Easy', 'PIA': 'Advanced', 'SSP': 'Advanced', 'DRP': 'Medium', 'BCP': 'Medium',
  'C4-CMP': 'Medium'
};

export function recommendArtifacts(brief, preset = 'Starter', showAll = false) {
  const pack = PRESET_PACKS[preset];
  const recommendations = [];
  const addedIds = new Set();
  
  // 1) Add core artifacts for the selected preset
  let selectedCount = 0;
  pack.coreArtifacts.forEach(acronym => {
    const artifact = taxonomyData.find(item => item.acronym === acronym);
    if (artifact && !addedIds.has(acronym)) {
      const isSelected = selectedCount < pack.maxAutoSelect;
      recommendations.push({
        id: acronym,
        name: `${acronym} — ${artifact.name}`,
        reason: artifact.meaning,
        selected: isSelected,
        difficulty: DIFFICULTY_MAP[acronym] || 'Medium',
        source: 'core',
        category: artifact.category
      });
      addedIds.add(acronym);
      if (isSelected) selectedCount++;
    } else if (!artifact) {
      console.warn(`Artifact ${acronym} not found in taxonomy`);
    }
  });
  
  // 2) Add cross-linked artifacts (unselected)
  if (showAll) {
    pack.coreArtifacts.forEach(acronym => {
      const artifact = taxonomyData.find(item => item.acronym === acronym);
      if (artifact && artifact.cross_links) {
        const crossLinks = artifact.cross_links.split(',').map(link => link.trim());
        crossLinks.forEach(linkAcronym => {
          const linkedArtifact = taxonomyData.find(item => item.acronym === linkAcronym);
          if (linkedArtifact && !addedIds.has(linkAcronym)) {
            recommendations.push({
              id: linkAcronym,
              name: `${linkAcronym} — ${linkedArtifact.name}`,
              reason: linkedArtifact.meaning,
              selected: false,
              difficulty: DIFFICULTY_MAP[linkAcronym] || 'Medium',
              source: 'related',
              category: linkedArtifact.category,
              parentId: acronym
            });
            addedIds.add(linkAcronym);
          }
        });
      }
    });
    
    // 3) Add remaining taxonomy artifacts
    taxonomyData.forEach(artifact => {
      if (!addedIds.has(artifact.acronym)) {
        recommendations.push({
          id: artifact.acronym,
          name: `${artifact.acronym} — ${artifact.name}`,
          reason: artifact.meaning,
          selected: false,
          difficulty: DIFFICULTY_MAP[artifact.acronym] || 'Medium',
          source: 'additional',
          category: artifact.category
        });
        addedIds.add(artifact.acronym);
      }
    });
  }
  
  const sortedRecommendations = recommendations.sort((a, b) => {
    if (a.selected !== b.selected) return b.selected - a.selected;
    const sourcePriority = { 'core': 1, 'related': 2, 'additional': 3 };
    const aPriority = sourcePriority[a.source] || 4;
    const bPriority = sourcePriority[b.source] || 4;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return a.name.localeCompare(b.name);
  });
  
  const actualSelectedCount = sortedRecommendations.filter(r => r.selected).length;
  
  return {
    recommendations: sortedRecommendations,
    preset: pack,
    selectedCount: actualSelectedCount,
    totalCount: recommendations.length
  };
}

export function getPresetFromBrief(brief) {
  const content = JSON.stringify(brief).toLowerCase();
  
  // Regulated indicators
  if (content.includes('banking') || content.includes('financial') || 
      content.includes('regulated') || content.includes('compliance') ||
      content.includes('pci') || content.includes('sox')) {
    return 'Regulated';
  }
  
  // Serious Product indicators  
  if (content.includes('payment') || content.includes('pii') || 
      content.includes('personal') || content.includes('healthcare') ||
      content.includes('gdpr') || content.includes('hipaa')) {
    return 'Serious';
  }
  
  return 'Starter';
}

export function getPresetOptions() {
  return Object.entries(PRESET_PACKS).map(([key, pack]) => ({
    value: key,
    label: pack.name,
    description: pack.description
  }));
}

export function getC4Bundle() {
  return C4_BUNDLE;
}

export function getThreatModelPreview() {
  return {
    title: "Threat Model Template Preview",
    sections: [
      "• Diagram — Link to C4/sequence diagrams",
      "• Assets/Trust Zones — Data classes, components", 
      "• Assumptions — Trusted boundaries, third parties",
      "• STRIDE Analysis:",
      "  - Spoofing: risk, control",
      "  - Tampering: risk, control", 
      "  - Repudiation: risk, control",
      "  - Information Disclosure: risk, control",
      "  - Denial of Service: risk, control",
      "  - Elevation of Privilege: risk, control",
      "• Residual Risks — List remaining risks",
      "• Controls/Test Evidence — How verified"
    ]
  };
}